import ToolBar from '@/components/ToolBar';
import { Form } from '@/components/ui/form';
import { useNavigate, useParams } from 'react-router-dom';
import { CourtForm, CourtSchema } from './CourtForm';
import AlertBox from '@/components/AlertBox';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useCourt, useUpdateCourt } from '@/services/courtService';
import { displayError } from '@/lib/error';
import Loader from '@/components/Loader';
import { NotFoundRedirectionWrapper } from '@/components/NotFoundRedirectWrapper';

const CourtUpdate = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [isCancelAlertOpen, setIsCancelAlertOpen] = useState<boolean>(false);
  const [isSaveAlertOpen, setIsSaveAlertBoxOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useCourt(id);
  const { mutate: updateCourt, status } = useUpdateCourt();
  const form = useForm<z.infer<typeof CourtSchema>>({
    resolver: zodResolver(CourtSchema),
    mode: 'onBlur',
  });

  const {
    getValues,
    reset,
    formState: { isValid, isDirty },
  } = form;

  const handleSubmit = () => {
    const values = getValues();
    updateCourt(
      {
        id: id!,
        data: values,
      },
      {
        onSuccess: (data) => {
          setIsSaveAlertBoxOpen(false);
          toast({
            variant: 'default',
            description: data.message,
          });
          navigate('/courts');
        },
        onError: (e) => {
          setIsSaveAlertBoxOpen(false);
          displayError(e, 5000);
        },
      }
    );
  };
  useEffect(() => {
    if (data) {
      const courtDetails = data;
      reset({
        name: courtDetails?.name ?? '',
        code: courtDetails?.code ?? '',
        county: courtDetails?.county ?? '',
        state: courtDetails?.state ?? '',
        type: courtDetails?.type ?? '',
      });
    }
  }, [data]);
  return (
    <NotFoundRedirectionWrapper isItemNotFound={isError} itemName="court">
      <Loader isLoading={isLoading}>
        <Form {...form}>
          <div className="flex flex-col w-full">
            <ToolBar title={data?.name} />
            <div className="flex flex-col gap-6">
              <CourtForm />
              <div className="flex gap-4 ">
                <AlertBox
                  triggerButton={
                    <Button
                      type="button"
                      variant="outline"
                      className="text-primary w-[75px] h-[36px] py-2 px-4 rounded-md focus:outline-none border-primary hover:bg-[#EDF5FF]"
                      onClick={() => setIsCancelAlertOpen(true)}
                    >
                      Cancel
                    </Button>
                  }
                  title="Are you sure you want to cancel?"
                  description="All of your current changes will be lost."
                  warning={true}
                  cancelBtnLabel="No"
                  confirmBtnLabel="Yes"
                  cancelBtnClick={() => setIsCancelAlertOpen(false)}
                  confirmBtnClick={() => navigate('/courts')}
                  open={isCancelAlertOpen}
                />
                <AlertBox
                  triggerButton={
                    <Button
                      variant="outline"
                      className="text-[#fff] h-[36px] w-[103px] py-2 px-4 rounded-md bg-primary border-primary hover:bg-[#12375C] hover:text-white"
                      disabled={!isValid || !isDirty}
                      onClick={() => setIsSaveAlertBoxOpen(true)}
                    >
                      Save
                    </Button>
                  }
                  title={`Are you sure?`}
                  description={`Would you like to update ${data?.name} details?`}
                  cancelBtnLabel="No"
                  confirmBtnLabel="Yes"
                  open={isSaveAlertOpen}
                  isLoading={status === 'pending'}
                  cancelBtnClick={() => setIsSaveAlertBoxOpen(false)}
                  confirmBtnClick={handleSubmit}
                />
              </div>
            </div>
          </div>
        </Form>
      </Loader>
    </NotFoundRedirectionWrapper>
  );
};

export default CourtUpdate;
